import { IInteractionMetricsSummary, InteractionMetric, TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { unreachableCaseError } from "@edgetier/utilities";
import { formatDateTickValue } from "utilities/format-date-tick-value";
import { ICombineData } from "./metric-comparison-chart.types";

/**
 * Combine the metric data for agents and overall for the chart.
 * @param metric      Metric to chart.
 * @param agentData   Interaction metric summaries for the agent.
 * @param overallData Interaction metric summaries for everyone.
 * @returns           Combined agent and overall data.
 */
export const combineData = (
    metric: InteractionMetric,
    agentData: IInteractionMetricsSummary[] | undefined,
    overallData: IInteractionMetricsSummary[] | undefined
): ICombineData[] => {
    if (!Array.isArray(agentData) || !Array.isArray(overallData) || agentData.length !== overallData.length) {
        return [];
    }

    return agentData.map((agentDatum, index) => ({
        agentValue: getMetricProperty(agentDatum, metric),
        dateTime: agentDatum.dateTime.getTime(),
        dateTimeString: formatDateTickValue(agentDatum.dateTime, TimePeriodGroupBy.Days),
        overallValue: getMetricProperty(overallData[index], metric),
    }));
};

/**
 * Tick interval for the first X axis
 * @param timePeriodGroup
 */
export const upperXAxisTickInterval = (timePeriodGroup: TimePeriodGroupBy) => {
    if (timePeriodGroup === TimePeriodGroupBy.Minutes) {
        return 9;
    }
    if (timePeriodGroup === TimePeriodGroupBy.Hours) {
        return 11;
    }
    if (timePeriodGroup === TimePeriodGroupBy.Months) {
        return "preserveStartEnd";
    }
    return "preserveStart";
};

/**
 * Tick interval for the first X axis
 * @param timePeriodGroup
 */
export const lowerXAxisTickInterval = (timePeriodGroup: TimePeriodGroupBy) => {
    if (timePeriodGroup === TimePeriodGroupBy.Hours || timePeriodGroup === TimePeriodGroupBy.Minutes) {
        return 4;
    }
    return 0;
};

/**
 * From a metric, get the summary value.
 * @param datum  Interaction metric summary row.
 * @param metric Metric being graphed.
 * @returns      Metric value.
 */
export const getMetricProperty = (datum: IInteractionMetricsSummary, metric: InteractionMetric) => {
    switch (metric) {
        case InteractionMetric.CustomerSatisfaction:
            return datum.customerSatisfactionScore;
        case InteractionMetric.ExperienceScore:
            return datum.meanExperienceScore;
        case InteractionMetric.FirstResponseTime:
            return datum.meanFirstResponseTimeSeconds;
        case InteractionMetric.HandlingTime:
            return datum.meanHandlingTimeSeconds;
        case InteractionMetric.NetPromoterScore:
            return datum.netPromoterScore;
        case InteractionMetric.QueueTime:
            return datum.meanQueueTimeSeconds;
        case InteractionMetric.AverageResponseTime:
            return datum.meanResponseSeconds;
        case InteractionMetric.QueryResolution:
            return datum.queryResolutionRate;
        case InteractionMetric.AgentMessagesCount:
            return datum.meanAgentMessagesCount;
        case InteractionMetric.BotMessagesCount:
            return datum.meanBotMessagesCount;
        case InteractionMetric.CustomerMessagesCount:
            return datum.meanCustomerMessagesCount;
        case InteractionMetric.MessagesCount:
            return datum.meanMessagesCount;
        case InteractionMetric.ServiceLevelAgreement:
            return datum.serviceLevelAgreementPercentage;
        case InteractionMetric.AgentFirstResponseTime:
            return datum.meanAgentFirstResponseTime;
        case InteractionMetric.AgentSatisfaction:
            return datum.meanAgentSatisfaction;
        case InteractionMetric.AgentScore:
            return datum.meanAgentScore;
        default:
            unreachableCaseError(metric);
            return 0;
    }
};
